import { action, computed, makeObservable, observable } from 'mobx'
import { debounce } from 'lodash'
import { dateValueOf, getLabelAsNumberInternationalFormat, logger } from 'shared/lib'
import { Base } from 'models'
import type {
  IResponseConversation,
  IResponseConversationDraftMessage,
  IResponseConversationDraftMessageContact,
  IResponseConversationDraftMessageData,
  IResponseConversationsFilterKey,
} from 'entities/Conversation/api/types'
import { Message } from 'entities/Message/model/Message'
import type { IResponseContact } from 'entities/Contacts/api/types'
import { type ConversationsStore } from 'entities/Conversation/store'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { eventEmitterStore } from 'entities/EventEmitter'
import { type IItemData } from 'entities/SmartViews/lib/checkFilterSmartView'
import { Phone } from 'entities/Phone/model/Phone'

export class Conversation extends Base {
  id: number
  contact_id!: number
  inbox_id!: number
  user_id!: number
  recent_message: Message | null = null
  recent_mention_message: Message | null = null
  last_message_at!: string
  unread_count!: number
  unread_count_prev!: number
  unread_mentions_count!: number
  last_manual_message_at!: string
  last_inbound_at!: string
  last_read_at!: string
  checked = false
  is_group!: boolean
  is_favorite!: boolean
  is_need_response!: boolean
  is_locked!: boolean
  is_lock_paused!: boolean
  group_name!: string
  closed_at!: string
  started_at!: string
  participantsIds!: number[]
  number_id!: number
  is_aircall!: boolean
  scheduled_message_ids!: number[]
  origin!: IResponseConversation
  debounceOnCreateDraft: ReturnType<typeof debounce>

  constructor(item: IResponseConversation, private _entities: IEntitiesStore) {
    super()

    this.debounceOnCreateDraft = debounce(this.onCreateDraft, 4000)
    this.id = item.id
    this.syncOrigin(item)

    makeObservable(this, {
      contact_id: observable,
      inbox_id: observable,
      closed_at: observable,
      started_at: observable,
      number_id: observable,
      is_aircall: observable,
      participantsIds: observable,
      is_group: observable,
      is_favorite: observable,
      is_need_response: observable,
      is_locked: observable,
      recent_message: observable,
      recent_mention_message: observable,
      scheduled_message_ids: observable,
      message: computed,
      handleChecked: action,
      checked: observable,
      last_message_at: observable,
      last_manual_message_at: observable,
      last_read_at: observable,
      lastMessageAtValueOf: computed,
      isUnread: computed,
      scheduleCount: computed,
      contactsIds: computed,
      priority: computed,
      user_id: observable,
      unread_count: observable,
      unread_count_prev: observable,
      unread_mentions_count: observable,
      is_lock_paused: observable,
    })
  }

  cancelDebounceOnCreateDraft = () => {
    this.debounceOnCreateDraft?.cancel()
  }

  syncOrigin = (origin: IResponseConversation) => {
    this.id = origin.id
    this.contact_id = origin.contact_id
    this.inbox_id = origin.inbox_id
    this.user_id = origin.user_id
    this.closed_at = origin.closed_at
    this.started_at = origin.started_at
    this.is_group = origin.is_group
    this.is_need_response = origin.is_need_response || false
    this.is_locked = origin.id === 0 ? false : origin.is_locked ?? true
    this.is_lock_paused = origin.is_lock_paused
    this.is_favorite =
      origin.is_favorite === null
        ? typeof this._entities.usersStore.user?.id === 'number'
          ? origin.favorite_for_users.includes(this._entities.usersStore.user?.id)
          : false
        : origin.is_favorite
    this.group_name = origin.group_name || ''
    const isVoid = origin.recent_message?.type === 'call' && !origin.recent_message.body
    if (origin.recent_message && !isVoid) {
      this.recent_message = new Message({ message: origin.recent_message }, this._entities)
    }

    const isMentionVoid =
      origin.recent_mention_message?.type === 'call' && !origin.recent_mention_message?.body
    if (origin.recent_mention_message && !isMentionVoid) {
      this.recent_mention_message = new Message(
        { message: origin.recent_mention_message },
        this._entities
      )
    }
    this.last_message_at = origin.last_message_at || ''
    this.last_manual_message_at = origin.last_manual_message_at || ''
    this.last_inbound_at = origin.last_inbound_at || ''
    this.last_read_at = origin.last_read_at || ''
    this.unread_count = origin.unread_count || 0
    this.unread_count_prev = origin.unread_count_prev || 0
    this.unread_mentions_count = origin.unread_mentions_count || 0
    this.participantsIds = Array.isArray(origin.participants)
      ? origin.participants.map((participant: IResponseContact) => participant.id)
      : []
    this.number_id = origin.number_id
    this.is_aircall = !!origin.number?.is_aircall
    this.scheduled_message_ids = origin.scheduled_message_ids || []
    this.origin = origin
  }

  handleChecked = (value?: boolean) => {
    const isBoolean = typeof value === 'boolean'
    if (isBoolean) {
      this.checked = value
      return
    }

    this.checked = !this.checked
  }

  setIsNeedResponse = (value: boolean) => {
    this.is_need_response = value
    this.origin.is_need_response = value
  }

  get smartViewFilterParams(): IItemData {
    const tagIds = this.origin.contact?.tags.map((item) => item.id) || []

    return {
      contactIds: [this.contact_id],
      inboxIds: [this.inbox_id],
      userIds: [this.user_id],
      tagIds: tagIds,
    }
  }

  get lastMessageAtValueOf() {
    return dateValueOf(this.last_message_at)
  }

  get unreadCount() {
    return this.unread_count + this.unread_mentions_count
  }

  get unreadMentionCount() {
    return this.unread_mentions_count || 0
  }

  get isUnreadManual() {
    return this.unreadCount === 0 && !this.last_read_at
  }

  get isUnread() {
    if (this.id === 0) return false
    if (this.isUnreadManual) return false
    if (!this.last_read_at) return true
    if (this.unreadCount > 0) return true

    return this.last_read_at < this.last_message_at
  }

  get isUnreadMention() {
    return this.unread_mentions_count > 0
  }

  get isNew() {
    return this.id === 0
  }

  get message() {
    return this.recent_message || null
  }

  get recentMentionMessage() {
    return this.recent_mention_message || null
  }

  get isStarted() {
    return !!this.started_at
  }

  get isClosed() {
    return !!this.closed_at
  }

  get isRead() {
    return !!this.last_read_at
  }

  get title() {
    const contact = this._entities.contactsStore.getItem(this.contact_id)

    if (this?.is_group) {
      if (this.group_name) return this.group_name

      const participants = this.participantsIds.map((id) =>
        this._entities.contactsStore.getItem(id)
      )

      return [contact, ...participants]
        .map(
          (participant) =>
            participant?.full_name ||
            (participant?.number && getLabelAsNumberInternationalFormat(participant.number))
        )
        .join(', ')
    }

    return (
      contact?.full_name || (contact?.number && getLabelAsNumberInternationalFormat(contact.number))
    )
  }

  get contactsIds() {
    return [this.contact_id, ...this.participantsIds]
  }

  get isGroup() {
    return Boolean(this.is_group)
  }

  get scheduleCount() {
    return this.scheduled_message_ids?.length
  }

  get priority() {
    return this.is_favorite
  }

  get contact() {
    return this._entities.contactsStore.getItem(this.contact_id)
  }

  get hasUnreadFilter() {
    return !this.isClosed && this.isUnread
  }

  get hasOutboundFilter() {
    return !this.isClosed && !this.isStarted
  }

  get hasAssignedFilter() {
    return this.isStarted && this._entities.usersStore.user?.id === this.user_id && !this.isClosed
  }

  get hasClosedFilter() {
    return this.isClosed
  }

  get hasUnassignedFilter() {
    return this.user_id === 0 && !this.isClosed
  }

  get hasBlockedFilter() {
    return !!this.contact?.is_blocked
  }

  get hasDraftFilter() {
    return this.origin.draft_message !== null
  }

  get hasMentionsFilter() {
    return !!this.origin.recent_mention_message
  }

  get concatUnread() {
    if (this.unread_count < this.unread_count_prev) {
      return -Math.abs(this.unread_count - this.unread_count_prev)
    }

    return Math.abs(this.unread_count - this.unread_count_prev)
  }

  get filterKey(): IResponseConversationsFilterKey {
    if (this.hasUnreadFilter) return 'unread'
    if (this.hasOutboundFilter) return 'outbound'
    if (this.hasAssignedFilter) return 'assigned'
    if (this.hasClosedFilter) return 'closed'
    if (this.hasUnassignedFilter) return 'unassigned'
    if (this.hasDraftFilter) return 'draft'
    if (this.hasMentionsFilter) return 'mentions'

    return 'open'
  }

  get number() {
    if (!this.origin.number) return undefined

    return new Phone(this.origin.number, this._entities)
  }

  onAddScheduledMessageId = (id: number) => {
    this.scheduled_message_ids.push(id)
  }

  onRemoveScheduledMessageId = (removeId: number) => {
    this.scheduled_message_ids = this.scheduled_message_ids.filter((id) => id !== removeId)
    this.origin.scheduled_message_ids = this.scheduled_message_ids
  }

  updatePriority = (priority: boolean) => {
    this.is_favorite = priority
  }

  onCreateDraft = (
    messageData: IResponseConversationDraftMessageData,
    conversationStore: ConversationsStore
  ) => {
    if (!this._entities.usersStore.user) return
    if (!this._entities.websocket.socket) return

    logger.info('CREATE [Draft]')

    const contacts = this.contactsIds.reduce<IResponseConversationDraftMessageContact[]>(
      (state, id) => {
        const contact = this._entities.contactsStore.getItem(id)

        return contact ? [contact.dataDraft, ...state] : state
      },
      []
    )
    const inbox = this._entities.inboxesStore.getItem(this.inbox_id)

    const draftLocal = {
      id: this._entities.usersStore.user.id,
      message: messageData,
      contacts: contacts,
      conversation_id: this.id,
      inbox_id: Number(inbox?.id),
      inbox_type: Number(inbox?.inboxType),
      socketId: String(this._entities.websocket.socket.sessionID),
    }

    conversationStore.addItemDraft(draftLocal)
    eventEmitterStore.emit('draftMessageCreated', { id: this.id })

    this._entities.websocket.sendEvent<IResponseConversationDraftMessage>(
      `private-user.${this._entities.usersStore.user.id}`,
      'client-draft',
      draftLocal
    )
  }
}
